/**
 * Technician session for the public /tech pages (the VeoTech apps' "Form Access"), ported from the
 * retired Home console's tech-mobile module so it behaves the same:
 * - the apps open `/tech#token=<technician JWT>`; the token moves to localStorage under the same
 *   `tech_*` keys Home used (same origin, so existing sessions carry over) and leaves the address;
 * - otherwise technicians sign in with phone + password + SMS code (/tech/login);
 * - calls go to the technician API on the environment's fargate host, which allows the home
 *   hosts' origins (CORS), so these pages are served from home(-dev|-staging).veoride.com.
 * Technician tokens stay apart from the admin session (different keys, never sent to /api/admins).
 */

export const TECH_ACCESS_TOKEN = 'tech_accessToken';
export const TECH_TOKEN_CREATE_DATE = 'tech_tokenCreateDate';
export const TECH_PHONE = 'tech_phone';

export const TECH_PATHS = { home: '/tech', login: '/tech/login' } as const;

export const techStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // private mode or quota: the session lasts this page only
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // nothing to clear
    }
  },
};

/** Stores a `#token=` handed over by the app and removes it from the address (and history). */
export function consumeHandoffToken(): void {
  const hash = window.location.hash;
  if (hash.length < 2) return;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('token');
  if (!token) return;
  techStorage.set(TECH_ACCESS_TOKEN, token);
  techStorage.set(TECH_TOKEN_CREATE_DATE, String(Date.now()));
  params.delete('token');
  const rest = params.toString();
  window.history.replaceState(null, '', window.location.pathname + window.location.search + (rest ? `#${rest}` : ''));
}

export function clearTechSession(withPhone = false): void {
  techStorage.remove(TECH_ACCESS_TOKEN);
  techStorage.remove(TECH_TOKEN_CREATE_DATE);
  if (withPhone) techStorage.remove(TECH_PHONE);
}

/** The technician API host for this page's host: home[-env] / manhattan[-env] -> fargate[-env]. */
export function techApiBase(hostname: string): string {
  const m = /^(?:home|manhattan)(-dev|-staging)?\.veoride\.com$/.exec(hostname);
  return m ? `https://fargate${m[1] ?? ''}.veoride.com` : ''; // local dev: same origin (proxy)
}

export class TechRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: number,
  ) {
    super(message);
    this.name = 'TechRequestError';
  }
}

/** Calls the technician API and unwraps its `{ code, msg, data }` envelope. */
export async function techRequest<T>(path: string, init: { method?: 'GET' | 'POST'; body?: unknown; auth?: boolean } = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = init;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = auth ? techStorage.get(TECH_ACCESS_TOKEN) : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(techApiBase(window.location.hostname) + path, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });
  let payload: { code?: number; msg?: string; message?: string; data?: T } | null = null;
  try {
    payload = await res.json();
  } catch {
    // empty or non-JSON body
  }
  const message = payload?.msg || payload?.message;
  if (!res.ok) throw new TechRequestError(message || res.statusText || 'Request failed', res.status, payload?.code);
  if (payload && typeof payload === 'object' && 'code' in payload && payload.code != null && payload.code !== 0 && payload.code !== 200) {
    throw new TechRequestError(message || 'Request failed', res.status, payload.code);
  }
  return (payload && typeof payload === 'object' && 'code' in payload ? payload.data : payload) as T;
}

export interface TechnicianProfile {
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: number;
  email?: string;
  areaId?: number;
  areaName?: string;
}

export const getCurrentTechnician = () => techRequest<TechnicianProfile>('/api/technicians');

export const requestAuthCode = (input: { phone: number; password: string }) =>
  techRequest<boolean>('/api/technicians/auth/auth-code', { method: 'POST', body: input, auth: false });

export const verifyAuthCode = (input: { phone: number; code: string }) =>
  techRequest<{ token: string; requirePasswordUpdate?: boolean }>('/api/technicians/auth/auth-code/verification', {
    method: 'POST',
    body: input,
    auth: false,
  });

/** The trailing 10 digits of a typed phone ("+1 (555) 555-1234" works), or null. */
export function parsePhone(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10) return null;
  return Number(digits.slice(-10));
}

/** 10-digit US numbers as (555) 555-1234; anything else as given. */
export function formatPhone(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '';
  const d = String(raw).replace(/\D/g, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(raw);
}

/** `name`, else first + last unless they are the backend's "Default" placeholders. */
export function displayName(p: TechnicianProfile | null): string {
  if (!p) return '';
  if (p.name?.trim()) return p.name.trim();
  return [p.firstName, p.lastName]
    .filter((s) => s && s !== 'Default')
    .join(' ')
    .trim();
}

/** Greeting key for the hour, as Home had it. */
export function greetingKey(hour: number): 'night' | 'morning' | 'afternoon' | 'evening' {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}
